/**
 * 日付フォーマット関数
 *
 * ISO 8601形式の日付文字列を、指定された言語に応じて
 * 適切な形式にフォーマットするユーティリティ関数を提供します。
 *
 * @see docs/system.md
 */

import type { SupportedLanguage } from './i18n-helpers';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** 日付解析に使用する正規表現パターン */
const DATE_PATTERNS = {
  yearOnly: /^(\d{4})$/,
  yearPrefix: /^(\d{4})/,
  isoDuration: /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/,
  decade: /^(\d{4})年代$/,
  vagueYear: /^(\d{4})(年頃|\?)$/,
  fullDate: /^(\d{4})-(\d{2})-(\d{2})/,
  yearMonth: /^(\d{4})-(\d{2})$/,
  jaYearMonth: /^(\d{4})年(\d{1,2})月/,
} as const;

/**
 * 日付文字列から年・月・日を抽出するパースヘルパー
 *
 * ISO 8601形式（YYYY-MM-DD, YYYY-MM, YYYY）および日本語形式（YYYY年M月）に対応。
 *
 * @param dateStr - 日付文字列
 * @returns 年・月・日のコンポーネント、または解析不可の場合 null
 */
function parseDateComponents(
  dateStr: string
): { year: number; month?: number; day?: number } | null {
  let match: RegExpExecArray | null;

  if (
    (match = DATE_PATTERNS.fullDate.exec(dateStr)) &&
    match[1] &&
    match[2] &&
    match[3]
  ) {
    return { year: +match[1], month: +match[2], day: +match[3] };
  }
  if ((match = DATE_PATTERNS.yearMonth.exec(dateStr)) && match[1] && match[2]) {
    return { year: +match[1], month: +match[2] };
  }
  if (
    (match = DATE_PATTERNS.jaYearMonth.exec(dateStr)) &&
    match[1] &&
    match[2]
  ) {
    return { year: +match[1], month: +match[2] };
  }
  if ((match = DATE_PATTERNS.yearOnly.exec(dateStr)) && match[1]) {
    return { year: +match[1] };
  }

  return null;
}

/**
 * ISO 8601形式の日付文字列を指定言語でフォーマット
 *
 * サポート形式:
 * - 年のみ: "1968"
 * - 年月: "1968-05"
 * - 完全日付: "1968-05-15"
 *
 * 出力形式:
 * - 日本語:
 *   - "1968" → "1968年"
 *   - "1968-05" → "1968年5月"
 *   - "1968-05-15" → "1968年5月15日"
 * - 英語:
 *   - "1968" → "1968"
 *   - "1968-05" → "May 1968"
 *   - "1968-05-15" → "May 15, 1968"
 *
 * @param isoDate - ISO 8601形式の日付文字列
 * @param lang - 言語コード ('ja' | 'en')
 * @returns フォーマット済み日付文字列
 *
 * @example
 * ```typescript
 * formatDate('1968', 'ja'); // => '1968年'
 * formatDate('1968-05-15', 'en'); // => 'May 15, 1968'
 * ```
 */
export function formatDate(isoDate: string, lang: SupportedLanguage): string {
  if (!isoDate) {
    return '';
  }

  // 年のみの場合
  if (DATE_PATTERNS.yearOnly.test(isoDate)) {
    return lang === 'ja' ? `${isoDate}年` : isoDate;
  }

  // 年月または完全日付の場合
  try {
    // ISO 8601形式を安全にパース
    // "1968-05" の場合、"-01" を追加して有効な日付にする
    const dateStr = isoDate.length === 7 ? `${isoDate}-01` : isoDate;
    const date = new Date(dateStr);

    // 無効な日付の場合は元の文字列を返す
    if (isNaN(date.getTime())) {
      return isoDate;
    }

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    if (lang === 'ja') {
      // 日本語フォーマット
      if (isoDate.length === 7) {
        // 年月: "1968年5月"
        return `${year}年${month}月`;
      } else {
        // 完全日付: "1968年5月15日"
        return `${year}年${month}月${day}日`;
      }
    } else {
      // 英語フォーマット
      const monthName = MONTH_NAMES[month - 1];

      if (isoDate.length === 7) {
        // 年月: "May 1968"
        return `${monthName} ${year}`;
      } else {
        // 完全日付: "May 15, 1968"
        return `${monthName} ${day}, ${year}`;
      }
    }
  } catch {
    // パースエラーの場合は元の文字列を返す
    return isoDate;
  }
}

/**
 * 日付範囲を指定言語でフォーマット
 *
 * 出力形式:
 * - 開始日のみ: formatDate() と同じ
 * - 開始日〜終了日（異なる年）:
 *   - 日本語: "1964年 - 1965年" / "1964年7月15日 - 1965年8月20日"
 *   - 英語: "1964 - 1965" / "July 15, 1964 - August 20, 1965"
 * - 開始日〜終了日（同年）:
 *   - 日本語: "1964年7月15日 - 8月20日"
 *   - 英語: "July 15 - August 20, 1964"
 *
 * @param startDate - 開始日（ISO 8601形式）
 * @param endDate - 終了日（ISO 8601形式、任意）
 * @param lang - 言語コード ('ja' | 'en')
 * @returns フォーマット済み日付範囲文字列
 *
 * @example
 * ```typescript
 * formatDateRange('1964', '1965', 'ja');
 * // => '1964年 - 1965年'
 *
 * formatDateRange('1964-07-15', '1964-08-20', 'en');
 * // => 'July 15 - August 20, 1964'
 * ```
 */
export function formatDateRange(
  startDate: string,
  endDate: string | undefined,
  lang: SupportedLanguage
): string {
  if (!startDate) {
    return '';
  }

  // 終了日が指定されていない場合は開始日のみフォーマット
  if (!endDate) {
    return formatDate(startDate, lang);
  }

  // 両方の日付をパース
  const startYear = startDate.slice(0, 4);
  const endYear = endDate.slice(0, 4);

  // 同年かどうかを判定
  const isSameYear = startYear === endYear;

  // 完全日付かどうかを判定
  const isFullDate = startDate.length === 10 && endDate.length === 10;

  if (isSameYear && isFullDate) {
    // 同年内の期間
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return `${formatDate(startDate, lang)} - ${formatDate(endDate, lang)}`;
      }

      const startMonth = start.getMonth() + 1;
      const startDay = start.getDate();
      const endMonth = end.getMonth() + 1;
      const endDay = end.getDate();

      if (lang === 'ja') {
        // "1964年7月15日 - 8月20日"
        return `${startYear}年${startMonth}月${startDay}日 - ${endMonth}月${endDay}日`;
      } else {
        // "July 15 - August 20, 1964"
        const startMonthName = MONTH_NAMES[startMonth - 1];
        const endMonthName = MONTH_NAMES[endMonth - 1];
        return `${startMonthName} ${startDay} - ${endMonthName} ${endDay}, ${startYear}`;
      }
    } catch {
      // パースエラーの場合は通常の範囲表記
      return `${formatDate(startDate, lang)} - ${formatDate(endDate, lang)}`;
    }
  } else {
    // 異なる年または年月のみの期間
    return `${formatDate(startDate, lang)} - ${formatDate(endDate, lang)}`;
  }
}

/**
 * 年代表記を生成
 *
 * @param year - 年（数値）
 * @param lang - 言語コード ('ja' | 'en')
 * @returns 年代表記文字列
 *
 * @example
 * ```typescript
 * formatDecade(1960, 'ja'); // => '1960年代'
 * formatDecade(1970, 'en'); // => '1970s'
 * ```
 */
export function formatDecade(year: number, lang: SupportedLanguage): string {
  const decade = Math.floor(year / 10) * 10;
  return lang === 'ja' ? `${decade}年代` : `${decade}s`;
}

/**
 * ISO 8601日付文字列から年を抽出
 *
 * @param isoDate - ISO 8601形式の日付文字列
 * @returns 年（数値）、パース失敗時はnull
 *
 * @example
 * ```typescript
 * extractYear('1968'); // => 1968
 * extractYear('1968-05-15'); // => 1968
 * ```
 */
export function extractYear(isoDate: string): number | null {
  if (!isoDate) {
    return null;
  }

  const match = isoDate.match(DATE_PATTERNS.yearPrefix);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }

  return null;
}

/**
 * ISO 8601 Duration形式を人間向けに変換
 *
 * サポート形式:
 * - "PT30S" → "30秒" / "30 sec"
 * - "PT60S" → "1分" / "1 min"
 * - "PT2M30S" → "2分30秒" / "2 min 30 sec"
 * - "PT12M" → "12分" / "12 min"
 * - 非ISO形式（"ループ"等）→ そのまま返却
 *
 * @param duration - ISO 8601 Duration形式の文字列
 * @param lang - 言語コード ('ja' | 'en')
 * @returns フォーマット済み文字列
 *
 * @example
 * ```typescript
 * formatDuration('PT2M30S', 'ja'); // => '2分30秒'
 * formatDuration('PT30S', 'en'); // => '30 sec'
 * ```
 */
export function formatDuration(
  duration: string,
  lang: SupportedLanguage
): string {
  if (!duration) return '';

  const match = duration.match(DATE_PATTERNS.isoDuration);
  if (!match) return duration; // "ループ"等はそのまま返却

  let hours = match[1] ? parseInt(match[1], 10) : 0;
  let minutes = match[2] ? parseInt(match[2], 10) : 0;
  let seconds = match[3] ? parseInt(match[3], 10) : 0;

  // 秒から分への繰り上げ
  if (seconds >= 60) {
    minutes += Math.floor(seconds / 60);
    seconds = seconds % 60;
  }

  // 分から時間への繰り上げ
  if (minutes >= 60) {
    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  const parts: string[] = [];
  if (lang === 'ja') {
    if (hours) parts.push(`${hours}時間`);
    if (minutes) parts.push(`${minutes}分`);
    if (seconds || parts.length === 0) parts.push(`${seconds}秒`);
  } else {
    if (hours) parts.push(`${hours} hr`);
    if (minutes) parts.push(`${minutes} min`);
    if (seconds || parts.length === 0) parts.push(`${seconds} sec`);
  }

  return parts.join(lang === 'ja' ? '' : ' ');
}

/**
 * 日付文字列からソート用の数値を抽出
 *
 * 年代範囲（"1970年代"等）は、その年代の最後（1979.9）として扱い、
 * 具体的な年（"1970"等）よりも後に配置される。
 *
 * 優先順位:
 * 1. 具体的な日付（"1970-05-15"） → 1970.0515
 * 2. 年月（"1970-05"） → 1970.05
 * 3. 年のみ（"1970"） → 1970.0
 * 4. 曖昧な年（"1970年頃", "1970?"） → 1970.5
 * 5. 年代範囲（"1970年代"） → 1979.9（その年代の最後）
 * 6. 不明・パース不可 → Infinity
 *
 * @param dateStr - 日付文字列
 * @returns ソート用の数値
 *
 * @example
 * ```typescript
 * getDateSortValue('1970'); // => 1970
 * getDateSortValue('1970年代'); // => 1979.9
 * ```
 */
export function getDateSortValue(dateStr: string): number {
  if (!dateStr) {
    return Infinity;
  }

  // 年代範囲パターン: "1970年代" → 1979.9
  const decadeMatch = DATE_PATTERNS.decade.exec(dateStr);
  if (decadeMatch && decadeMatch[1]) {
    return parseInt(decadeMatch[1], 10) + 9.9;
  }

  // 曖昧な年パターン: "1970年頃", "1970?" → 1970.5
  const vagueMatch = DATE_PATTERNS.vagueYear.exec(dateStr);
  if (vagueMatch && vagueMatch[1]) {
    return parseInt(vagueMatch[1], 10) + 0.5;
  }

  // 年・月・日コンポーネントからソート値を算出
  const components = parseDateComponents(dateStr);
  if (!components) {
    return Infinity;
  }

  const { year, month, day } = components;
  if (day !== undefined) return year + (month ?? 0) / 100 + day / 10000;
  if (month !== undefined) return year + month / 100;
  return year;
}

/**
 * 日付文字列をソートするための比較関数
 *
 * @param dateA - 日付文字列A
 * @param dateB - 日付文字列B
 * @returns 比較結果（負: A < B、0: A = B、正: A > B）
 *
 * @example
 * ```typescript
 * compareDates('1970', '1970年代'); // => 負の値（1970が先）
 * compareDates('1970年代', '1980'); // => 負の値（1970年代が先）
 * ```
 */
export function compareDates(dateA: string, dateB: string): number {
  return getDateSortValue(dateA) - getDateSortValue(dateB);
}
